import type { PostType } from "./types.js";
import type { SocialPlatform } from "@content-os/config";
import { POST_TYPE_REGISTRY } from "./registry.js";

const VIDEO_URL_RE = /\.(mp4|mov|webm|m4v)(\?|$)/i;

/** True when a media URL points at a video file, judged by extension. */
export function isVideoUrl(url: string): boolean {
  return VIDEO_URL_RE.test(url);
}

/**
 * Cover frame offset (ms) sent for a video post that has no cover image and no
 * explicit cover setting (Instagram Reel `thumbOffset`, TikTok
 * `video_cover_timestamp_ms`). The platforms otherwise use frame 0, which is
 * often a blank fade-in. 1s matches the poster frame the web upload captures
 * (apps/web/src/lib/video-poster.ts), so the cover looks like the app's preview.
 */
export const DEFAULT_VIDEO_COVER_OFFSET_MS = 1000;

/**
 * A YouTube title from a caption: the first non-empty line with hashtags
 * removed and whitespace collapsed, capped at YouTube's 100-character limit.
 */
export function titleFromCaption(caption: string | null | undefined, max = 100): string {
  const firstLine = (caption ?? "").split(/\r?\n/).find((line) => line.trim().length > 0) ?? "";
  const cleaned = firstLine.replace(/#[\p{L}\p{N}_]+/gu, "").replace(/\s+/g, " ").trim();
  if (!cleaned) return "Untitled Short";
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trimEnd()}…`;
}

/** 9:16 vertical — the ratio Stories/Reels expect. */
export const VERTICAL_9_16 = 9 / 16; // 0.5625

/** Tolerance (±3%) for treating a measured ratio as matching a target. */
export const RATIO_TOLERANCE = 0.03;

export interface MediaDimensions {
  kind: "image" | "video";
  width?: number | null;
  height?: number | null;
}

export type RatioStatus = "match" | "mismatch" | "unknown";

export interface FormatChoice {
  /** null = platform default (feed / reel as the platform decides). */
  postType: PostType | null;
  label: string;
  isStory: boolean;
  ratioStatus: RatioStatus;
}

export function aspectRatioOf(media: { width?: number | null; height?: number | null }): number | null {
  const w = media.width ?? 0;
  const h = media.height ?? 0;
  if (!w || !h) return null;
  return w / h;
}

export function ratioMatches(
  ratio: number | null,
  target: number,
  tolerance: number = RATIO_TOLERANCE,
): boolean {
  if (ratio === null) return false;
  return Math.abs(ratio - target) <= target * tolerance;
}

/**
 * The Story PostType for a platform, or null if it has none. Derived from the
 * registry (single source of truth) — a new Story-capable platform only needs
 * `isStory: true` on its registry entry, no edit here.
 */
export function storyPostTypeFor(platform: SocialPlatform): PostType | null {
  const spec = Object.values(POST_TYPE_REGISTRY).find(
    (s) => s.platform === platform && s.isStory,
  );
  return spec ? spec.id : null;
}

/**
 * Format choices to offer for a platform given the attached media. Always
 * includes a default; adds a Story choice when the platform supports Stories
 * AND there is an image or video. Ratio only sets ratioStatus — Story stays
 * offered even on a mismatch (warn, allow anyway).
 */
export function eligibleFormats(
  platform: SocialPlatform,
  media: MediaDimensions | null,
): FormatChoice[] {
  const choices: FormatChoice[] = [
    { postType: null, label: "Feed / Reel", isStory: false, ratioStatus: "unknown" },
  ];
  const story = storyPostTypeFor(platform);
  if (story && media) {
    const ratio = aspectRatioOf(media);
    const ratioStatus: RatioStatus =
      ratio === null ? "unknown" : ratioMatches(ratio, VERTICAL_9_16) ? "match" : "mismatch";
    choices.push({ postType: story, label: "Story", isStory: true, ratioStatus });
  }
  return choices;
}
